import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import i18n from 'i18next';
import { subscriptionsApi, billingApi, formatApiError } from '../../lib/api';
import { getPlanLabel, PLAN_LABELS } from '../../lib/planLabels';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import PricingCards from '../../components/PricingCards';
import PaymentProviderSelector from '../../components/PaymentProviderSelector';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Check, CreditCard, Loader2, Zap, AlertCircle, Users, Calendar, BarChart3, ArrowUpRight, ArrowDownRight, Info,
    Search, ArrowUp, ArrowDown, ArrowUpDown, X, Filter,
} from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Progress } from '../../components/ui/progress';

export default function SubscriptionPage() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const [plans, setPlans] = useState({});
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checkingPayment, setCheckingPayment] = useState(false);
    const [processingPlan, setProcessingPlan] = useState(null);
    const [confirmChange, setConfirmChange] = useState(null);
    const [validationData, setValidationData] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loadingTrans, setLoadingTrans] = useState(false);
    const [processingPack, setProcessingPack] = useState(null);

    // Multi-provider payment
    const [providers, setProviders] = useState([]);
    const [paymentDialog, setPaymentDialog] = useState(null); // { mode, planId?, packId?, currency }
    const [selectedProvider, setSelectedProvider] = useState('stripe');

    // Mes factures: sort + filters + search
    const [invoiceSort, setInvoiceSort] = useState({ by: 'created_at', order: 'desc' });
    const [invoiceFilters, setInvoiceFilters] = useState({ status: 'all', plan: 'all' });
    const [invoiceSearch, setInvoiceSearch] = useState('');

    useEffect(() => {
        loadData();
        loadTransactions();
        billingApi.listProviders().then(res => {
            setProviders(res.data?.providers || []);
        }).catch(() => {
            // silent — dialog will show "no provider available"
        });
    }, []);

    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (sessionId) {
            checkPaymentStatus(sessionId);
        }
    }, [searchParams]);

    useEffect(() => {
        const overageSessionId = searchParams.get('overage_session_id');
        const overageCanceled = searchParams.get('overage_canceled');
        if (overageSessionId) {
            toast.success(t('subscription.overage_purchase_success'));
            // Reload usage so the new credits are reflected in real time.
            loadData();
            loadTransactions();
        } else if (overageCanceled) {
            toast.info(t('subscription.overage_purchase_canceled'));
        }
    }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    // Smooth-scroll to #overage when the URL hash points there (used by the
    // role-aware "quota reached" dialog for recruiters).
    useEffect(() => {
        if (!loading && window.location.hash === '#overage') {
            const t1 = window.setTimeout(() => {
                const el = document.getElementById('overage');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 200);
            return () => window.clearTimeout(t1);
        }
    }, [loading]);

    const loadData = async () => {
        try {
            const [plansRes, subscriptionRes] = await Promise.all([
                subscriptionsApi.getPlans(),
                subscriptionsApi.getCurrent()
            ]);
            setPlans(plansRes.data);
            setCurrentSubscription(subscriptionRes.data);
        } catch (error) {
            toast.error(t('subscription.load_error'));
        } finally {
            setLoading(false);
        }
    };

    const loadTransactions = async () => {
        if (!['admin', 'manager', 'billing_admin'].includes(user?.role)) return;
        setLoadingTrans(true);
        try {
            const res = await subscriptionsApi.getTransactions(0, 20);
            setTransactions(res.data?.transactions || []);
        } catch {
            // silent
        } finally {
            setLoadingTrans(false);
        }
    };

    const checkPaymentStatus = async (sessionId, attempts = 0) => {
        if (attempts >= 5) {
            toast.error(t('subscription.verify_failed'));
            setCheckingPayment(false);
            return;
        }
        setCheckingPayment(true);
        try {
            const response = await subscriptionsApi.getCheckoutStatus(sessionId);
            if (response.data.payment_status === 'paid') {
                toast.success(t('subscription.payment_success'));
                loadData();
                setCheckingPayment(false);
                window.history.replaceState({}, '', window.location.pathname);
            } else if (response.data.status === 'expired') {
                toast.error(t('subscription.session_expired'));
                setCheckingPayment(false);
            } else {
                setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
            }
        } catch (error) {
            setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
        }
    };

    const handleUpgrade = async (planId) => {
        if (planId === currentSubscription?.plan) return;
        setProcessingPlan(planId);

        try {
            // Validate the change first
            const validation = await subscriptionsApi.validateChange(planId);
            const v = validation.data;

            if (!v.valid) {
                toast.error(v.blockers.join('. '));
                setProcessingPlan(null);
                return;
            }

            // Show confirmation dialog with impact
            setValidationData(v);
            setConfirmChange(planId);
            setProcessingPlan(null);
        } catch (error) {
            toast.error(formatApiError(error));
            setProcessingPlan(null);
        }
    };

    const handleConfirmChange = async () => {
        const planId = confirmChange;
        setConfirmChange(null);
        setValidationData(null);
        setProcessingPlan(planId);

        const plan = plans[planId];
        const currentPlanId = currentSubscription?.plan;
        const hasStripeSubscription = !!currentSubscription?.stripe_subscription_id;

        try {
            if (planId === 'free') {
                // Downgrade to free → cancel-at-period-end via Stripe (handled in backend)
                const response = await subscriptionsApi.changePlan(planId);
                toast.success(response.data?.message || t('subscription.cancel_scheduled'));
                loadData();
            } else if (plan && plan.price > 0 && hasStripeSubscription && currentPlanId !== 'free') {
                // Already subscribed → modify via Stripe (proration handled backend)
                const response = await billingApi.changeSubscription(planId);
                toast.success(response.data.message || t('subscription.subscription_updated'));
                loadData();
            } else {
                // First paid subscription → ask user which payment method to use
                openPaymentDialog({ mode: 'subscription', planId, currency: 'eur' });
                setProcessingPlan(null);
                return;
            }
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setProcessingPlan(null);
        }
    };

    const openPaymentDialog = (payload) => {
        // Default provider = first CONFIGURED provider that supports this mode
        const eligible = (providers || []).filter((p) => {
            const capOK = payload.mode === 'subscription' ? p.supports_subscriptions : p.supports_one_time;
            const curOK = (p.supported_currencies || []).includes((payload.currency || 'eur').toLowerCase());
            return capOK && curOK && p.configured;
        });
        setSelectedProvider(eligible[0]?.id || 'stripe');
        setPaymentDialog(payload);
    };

    const confirmPaymentDialog = async () => {
        if (!paymentDialog) return;
        const { mode, planId, packId, currency } = paymentDialog;
        const providerId = selectedProvider;
        try {
            if (mode === 'subscription' && planId) {
                setProcessingPlan(planId);
                setPaymentDialog(null);
                const response = await billingApi.createCheckoutSession(planId, window.location.origin, providerId, currency);
                if (response.data?.url) {
                    window.location.href = response.data.url;
                }
            } else if (mode === 'one_time' && packId) {
                setProcessingPack(packId);
                setPaymentDialog(null);
                const response = await billingApi.createOverageCheckout(packId, window.location.origin, providerId, currency);
                if (response.data?.url) {
                    window.location.href = response.data.url;
                } else {
                    throw new Error('Provider URL missing');
                }
            }
        } catch (error) {
            toast.error(formatApiError(error));
            setProcessingPlan(null);
            setProcessingPack(null);
        }
    };

    const handleOpenPortal = async () => {
        try {
            const returnUrl = window.location.href;
            const response = await billingApi.createPortalSession(returnUrl);
            if (response.data.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            toast.error(formatApiError(error));
        }
    };

    const handlePurchaseOverage = async (packId) => {
        if (processingPack) return;
        openPaymentDialog({ mode: 'one_time', packId, currency: 'eur' });
    };

    const usage = currentSubscription?.usage || {};
    const analysisPercent = usage.analysis_percent || 0;
    const isWarning = analysisPercent >= 80 && analysisPercent < 100;
    const extractionPercent = usage.extraction_percent || 0;
    const isExtractionExceeded = usage.extraction_quota_exceeded;
    const isExtractionWarning = extractionPercent >= 70 && extractionPercent < 100;

    // Filtered + sorted invoices (client-side; dataset is small)
    const filteredInvoices = useMemo(() => {
        let list = transactions;
        if (invoiceFilters.status !== 'all') {
            list = list.filter((t) => t.payment_status === invoiceFilters.status);
        }
        if (invoiceFilters.plan !== 'all') {
            list = list.filter((t) => t.plan_id === invoiceFilters.plan);
        }
        if (invoiceSearch) {
            const q = invoiceSearch.toLowerCase();
            list = list.filter((t) =>
                (t.session_id || '').toLowerCase().includes(q) ||
                (t.plan_id || '').toLowerCase().includes(q) ||
                (t.organization_name || '').toLowerCase().includes(q)
            );
        }
        const { by, order } = invoiceSort;
        const dir = order === 'asc' ? 1 : -1;
        list = [...list].sort((a, b) => {
            const va = a?.[by];
            const vb = b?.[by];
            if (va == null && vb == null) return 0;
            if (va == null) return 1 * dir;
            if (vb == null) return -1 * dir;
            if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
            return String(va).localeCompare(String(vb)) * dir;
        });
        return list;
    }, [transactions, invoiceFilters, invoiceSearch, invoiceSort]);

    const invoiceFilterCount = (invoiceFilters.status !== 'all' ? 1 : 0) + (invoiceFilters.plan !== 'all' ? 1 : 0);
    const setInvoiceSortFor = (by) => {
        setInvoiceSort((s) => (s.by === by ? { by, order: s.order === 'asc' ? 'desc' : 'asc' } : { by, order: 'asc' }));
    };
    const invoiceSortIcon = (by) => {
        if (invoiceSort.by !== by) return ArrowUpDown;
        return invoiceSort.order === 'asc' ? ArrowUp : ArrowDown;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
            </div>
        );
    }
    const isExceeded = usage.quota_exceeded;

    return (
        <div className="max-w-4xl mx-auto space-y-8" data-testid="subscription-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">{t('subscription.page_title')}</h1>
                    <p className="text-[#4B5563]">
                        {t('subscription.subtitle')}
                        {currentSubscription?.organization_name && (
                            <span className="font-medium"> — {currentSubscription.organization_name}</span>
                        )}
                    </p>
                </div>
                {['admin', 'manager', 'billing_admin'].includes(user?.role) && currentSubscription?.stripe_customer_id && (
                    <Button
                        variant="outline"
                        onClick={handleOpenPortal}
                        data-testid="open-stripe-portal-btn"
                    >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {t('subscription.billing_portal')}
                    </Button>
                )}
            </div>

            {user?.role === 'recruiter' && (
                <div
                    className="flex items-start gap-3 p-4 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg"
                    data-testid="recruiter-readonly-alert"
                >
                    <AlertCircle className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-medium text-[#92400E]">{t('subscription.readonly_title')}</p>
                        <p className="text-sm text-[#92400E]">
                            {t('subscription.readonly_desc')}
                        </p>
                    </div>
                </div>
            )}

            {checkingPayment && (
                <div className="bg-[#DBEAFE] border border-[#3B82F6]/30 rounded-lg p-4 flex items-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-[#3B82F6]" />
                    <p className="text-[#1D4ED8]">{t('subscription.checking_payment')}</p>
                </div>
            )}

            {/* Current usage dashboard */}
            {currentSubscription && (
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB] space-y-6" data-testid="usage-dashboard">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                            {t('subscription.current_plan', { name: getPlanLabel(currentSubscription.plan) })}
                        </h2>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            currentSubscription.plan === 'enterprise' ? 'bg-[#DBEAFE] text-[#002FA7]' :
                            currentSubscription.plan === 'pro' ? 'bg-[#D1FAE5] text-[#059669]' :
                            'bg-[#F3F4F6] text-[#4B5563]'
                        }`}>
                            {getPlanLabel(currentSubscription.plan)}
                        </span>
                    </div>

                    {/* Stats cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                        {/* CV Import count (historical, never blocks) */}
                        <div className="p-4 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB]" data-testid="cv-import-card">
                            <div className="flex items-center gap-2 mb-2">
                                <BarChart3 className="w-4 h-4 text-[#002FA7]" />
                                <span className="text-sm font-medium text-[#4B5563]">{t('subscription.cv_imported')}</span>
                            </div>
                            <p className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">
                                {usage.import_count || 0}
                            </p>
                            <p className="text-[10px] text-[#9CA3AF] mt-1">{t('subscription.cv_imported_hint')}</p>
                        </div>

                        {/* Analysis quota (this one blocks) */}
                        <div className={`p-4 rounded-lg border ${isExceeded ? 'border-[#EF4444] bg-[#FEF2F2]' : isWarning ? 'border-[#F59E0B] bg-[#FFFBEB]' : 'border-[#E5E7EB] bg-[#F9FAFB]'}`} data-testid="cv-analysis-card">
                            <div className="flex items-center gap-2 mb-2">
                                <Check className={`w-4 h-4 ${isExceeded ? 'text-[#EF4444]' : isWarning ? 'text-[#F59E0B]' : 'text-[#10B981]'}`} />
                                <span className="text-sm font-medium text-[#4B5563]">{t('subscription.cv_analyzed_quota')}</span>
                            </div>
                            <p className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">
                                {usage.analysis_count || 0} <span className="text-base font-normal text-[#9CA3AF]">/ {usage.max_cvs || 0}</span>
                            </p>
                            {(usage.overage_credits ?? 0) > 0 && (
                                <p
                                    data-testid="overage-breakdown"
                                    className="text-[10px] text-[#059669] mt-1 font-medium"
                                >
                                    {t('subscription.overage_breakdown', {
                                        plan: usage.plan_max_cvs ?? 0,
                                        overage: usage.overage_credits ?? 0,
                                    })}
                                </p>
                            )}
                            <Progress value={analysisPercent} className={`h-2 mt-2 ${isExceeded ? '[&>div]:bg-[#EF4444]' : isWarning ? '[&>div]:bg-[#F59E0B]' : ''}`} />
                            {usage.monthly_reset && (
                                <p className="text-[10px] text-[#9CA3AF] mt-1">{t('subscription.monthly_reset')}</p>
                            )}
                        </div>

                        {/* Extraction quota (ratio 2:1 vs scoring) */}
                        <div
                            className={`p-4 rounded-lg border ${isExtractionExceeded ? 'border-[#EF4444] bg-[#FEF2F2]' : isExtractionWarning ? 'border-[#F59E0B] bg-[#FFFBEB]' : 'border-[#E5E7EB] bg-[#F9FAFB]'}`}
                            data-testid="cv-extraction-card"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <BarChart3 className={`w-4 h-4 ${isExtractionExceeded ? 'text-[#EF4444]' : isExtractionWarning ? 'text-[#F59E0B]' : 'text-[#059669]'}`} />
                                <span className="text-sm font-medium text-[#4B5563]">{t('subscription.cv_extracted_quota', 'Extractions IA')}</span>
                            </div>
                            <p className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">
                                {usage.extraction_count || 0} <span className="text-base font-normal text-[#9CA3AF]">/ {usage.max_extractions || 0}</span>
                            </p>
                            {(usage.extraction_overage_credits ?? 0) > 0 && (
                                <p
                                    data-testid="extraction-overage-breakdown"
                                    className="text-[10px] text-[#059669] mt-1 font-medium"
                                >
                                    {t('subscription.extraction_overage_breakdown', {
                                        defaultValue: '{{plan}} plan + {{overage}} pack (ratio 2:1)',
                                        plan: usage.plan_max_extractions ?? 0,
                                        overage: usage.extraction_overage_credits ?? 0,
                                    })}
                                </p>
                            )}
                            <Progress
                                value={extractionPercent}
                                className={`h-2 mt-2 ${isExtractionExceeded ? '[&>div]:bg-[#EF4444]' : isExtractionWarning ? '[&>div]:bg-[#F59E0B]' : '[&>div]:bg-[#059669]'}`}
                            />
                            <p className="text-[10px] text-[#9CA3AF] mt-1">
                                {t('subscription.extraction_hint', 'Débitée uniquement sur nouveau CV (jamais sur doublon ni cache)')}
                            </p>
                        </div>

                        {/* Users */}
                        <div className={`p-4 rounded-lg border ${usage.users_limit_reached ? 'border-[#F59E0B] bg-[#FFFBEB]' : 'border-[#E5E7EB] bg-[#F9FAFB]'}`} data-testid="users-usage-card">
                            <div className="flex items-center gap-2 mb-2">
                                <Users className="w-4 h-4 text-[#002FA7]" />
                                <span className="text-sm font-medium text-[#4B5563]">{t('subscription.users_label')}</span>
                            </div>
                            <p className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">
                                {usage.members_count || 0} <span className="text-base font-normal text-[#9CA3AF]">/ {usage.max_users || 0}</span>
                            </p>
                        </div>

                        {/* Reset date */}
                        <div className="p-4 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB]" data-testid="reset-date-card">
                            <div className="flex items-center gap-2 mb-2">
                                <Calendar className="w-4 h-4 text-[#002FA7]" />
                                <span className="text-sm font-medium text-[#4B5563]">
                                    {usage.monthly_reset ? t('subscription.next_reset') : t('subscription.quota_type')}
                                </span>
                            </div>
                            <p className="text-lg font-bold text-[#0A0A0A] font-['Outfit']">
                                {usage.monthly_reset && usage.quota_reset_date
                                    ? new Date(usage.quota_reset_date).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
                                    : t('subscription.fixed_quota')}
                            </p>
                        </div>
                    </div>

                    {/* Alerts — precise per-counter for free plan */}
                    {(usage.free_trial_exhausted_scoring || usage.free_trial_exhausted_extraction) && (
                        <div
                            className="flex items-start gap-3 p-4 bg-[#FEF2F2] border border-[#FECACA] rounded-lg"
                            data-testid="free-trial-exhausted-alert"
                        >
                            <AlertCircle className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="font-medium text-[#DC2626]">
                                    {usage.free_trial_exhausted_scoring && usage.free_trial_exhausted_extraction
                                        ? t('subscription.trial_exhausted_title')
                                        : usage.free_trial_exhausted_scoring
                                            ? t('subscription.trial_exhausted_scoring_title', {
                                                defaultValue: 'Quota Scoring atteint',
                                            })
                                            : t('subscription.trial_exhausted_extraction_title', {
                                                defaultValue: "Quota Extraction atteint",
                                            })}
                                </p>
                                <p className="text-sm text-[#EF4444] mt-1">
                                    {usage.free_trial_exhausted_scoring && usage.free_trial_exhausted_extraction
                                        ? t('subscription.trial_exhausted_desc')
                                        : usage.free_trial_exhausted_scoring
                                            ? t('subscription.trial_exhausted_scoring_desc', {
                                                defaultValue: 'Vous avez utilisé les {{max}} scorings de votre essai gratuit. Passez à un plan payant ou achetez un pack pour continuer.',
                                                max: usage.plan_max_cvs,
                                            })
                                            : t('subscription.trial_exhausted_extraction_desc', {
                                                defaultValue: 'Vous avez utilisé les {{max}} extractions de votre essai gratuit. Passez à un plan payant ou achetez un pack (chaque pack apporte +2 extractions par scoring).',
                                                max: usage.plan_max_extractions,
                                            })}
                                </p>
                            </div>
                        </div>
                    )}
                    {isExceeded && !usage.free_trial_exhausted_scoring && !usage.free_trial_exhausted_extraction && (
                        <div className="p-4 bg-[#FEF2F2] border border-[#FECACA] rounded-lg space-y-3" data-testid="quota-exceeded-alert">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="font-medium text-[#DC2626]">{t('quota_reached.title')}</p>
                                    <p className="text-sm text-[#EF4444] mt-1">
                                        {['admin', 'manager', 'billing_admin'].includes(user?.role)
                                            ? (currentSubscription?.plan === 'free'
                                                ? t('quota_reached.desc_free_billing')
                                                : t('quota_reached.desc_billing'))
                                            : t('quota_reached.desc_recruiter')}
                                    </p>
                                </div>
                            </div>
                            {['admin', 'manager', 'billing_admin'].includes(user?.role) && (
                                <div className="flex flex-col sm:flex-row gap-2 pl-8">
                                    {currentSubscription?.plan === 'free' ? (
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={() => {
                                                const el = document.querySelector('[data-testid="pricing-cards"]')
                                                    || document.getElementById('overage');
                                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                            }}
                                            data-testid="quota-alert-see-plans"
                                            className="bg-[#002FA7] hover:bg-[#002480] text-white"
                                        >
                                            <ArrowUpRight className="w-4 h-4 mr-2" />
                                            {t('quota_reached.see_plans')}
                                        </Button>
                                    ) : (
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={() => handlePurchaseOverage('pack_50')}
                                            disabled={Boolean(processingPack)}
                                            data-testid="quota-alert-buy-pack50"
                                            className="bg-[#002FA7] hover:bg-[#002480] text-white"
                                        >
                                            {processingPack === 'pack_50' ? (
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <Zap className="w-4 h-4 mr-2" />
                                            )}
                                            {t('quota_reached.buy_pack_50')}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {isWarning && !isExceeded && (
                        <div className="flex items-center gap-3 p-4 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg" data-testid="quota-warning-alert">
                            <AlertCircle className="w-5 h-5 text-[#F59E0B] flex-shrink-0" />
                            <div>
                                <p className="font-medium text-[#D97706]">{t('subscription.quota_warning_title', { percent: analysisPercent })}</p>
                                <p className="text-sm text-[#D97706]">
                                    {t('subscription.quota_warning_desc')}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Plans (unified component) */}
            <PricingCards
                variant="subscription"
                plansData={plans}
                currentPlan={currentSubscription?.plan}
                processingPlan={processingPlan}
                onUpgrade={handleUpgrade}
                canManageBilling={['admin', 'manager', 'billing_admin'].includes(user?.role)}
                onPurchaseOverage={handlePurchaseOverage}
                processingPack={processingPack}
            />

            {/* Confirmation Dialog */}
            <AlertDialog open={!!confirmChange} onOpenChange={() => { setConfirmChange(null); setValidationData(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            {validationData?.is_upgrade ? (
                                <ArrowUpRight className="w-5 h-5 text-[#10B981]" />
                            ) : (
                                <ArrowDownRight className="w-5 h-5 text-[#F59E0B]" />
                            )}
                            {validationData?.is_upgrade ? t('subscription.upgrade_label') : t('subscription.downgrade_label')} {t('subscription.to_label')} {getPlanLabel(confirmChange)}
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 mt-2">
                                <p className="text-[#4B5563]">{t('subscription.applies_all_users')}</p>
                                <div className="bg-[#F9FAFB] p-3 rounded-lg text-sm space-y-1">
                                    <p><strong>{t('subscription.quota_change')}</strong> {validationData?.impact?.max_cvs_change}</p>
                                    <p><strong>{t('subscription.users_change')}</strong> {validationData?.impact?.max_users_change}</p>
                                    <p><strong>{t('subscription.current_users')}</strong> {validationData?.impact?.current_members}</p>
                                </div>
                                {validationData?.warnings?.length > 0 && (
                                    <div className="flex items-start gap-2 p-3 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg">
                                        <AlertCircle className="w-4 h-4 text-[#F59E0B] mt-0.5 flex-shrink-0" />
                                        <p className="text-sm text-[#D97706]">{validationData.warnings[0]}</p>
                                    </div>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('subscription.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmChange} className="bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="confirm-plan-change-btn">
                            {t('subscription.confirm_change')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Invoices / Transactions */}
            {['admin', 'manager', 'billing_admin'].includes(user?.role) && (
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]" data-testid="billing-invoices">
                    <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4 flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-[#002FA7]" />
                        {t('subscription.invoices_title')}
                    </h2>
                    {loadingTrans ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-5 h-5 animate-spin text-[#002FA7]" />
                        </div>
                    ) : transactions.length === 0 ? (
                        <p className="text-sm text-[#9CA3AF] py-6 text-center">{t('subscription.no_invoices')}</p>
                    ) : (
                        <div className="space-y-3">
                            {/* Filters + search */}
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="relative flex-1 min-w-[220px]">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                                    <Input
                                        placeholder={t('subscription.invoice_search')}
                                        value={invoiceSearch}
                                        onChange={(e) => setInvoiceSearch(e.target.value)}
                                        className="pl-10 h-9"
                                        data-testid="invoices-search"
                                    />
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-[#9CA3AF] ml-2"><Filter className="w-3.5 h-3.5" />{t('admin.filters')}</div>
                                <Select value={invoiceFilters.status} onValueChange={(v) => setInvoiceFilters((p) => ({ ...p, status: v }))}>
                                    <SelectTrigger className="w-[150px] h-9 text-xs" data-testid="invoices-filter-status">
                                        <SelectValue placeholder={t('admin.col_status')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('subscription.all_statuses')}</SelectItem>
                                        <SelectItem value="paid">{t('subscription.status_paid')}</SelectItem>
                                        <SelectItem value="succeeded">{t('subscription.status_succeeded')}</SelectItem>
                                        <SelectItem value="pending">{t('subscription.status_pending')}</SelectItem>
                                        <SelectItem value="failed">{t('subscription.status_failed')}</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={invoiceFilters.plan} onValueChange={(v) => setInvoiceFilters((p) => ({ ...p, plan: v }))}>
                                    <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="invoices-filter-plan">
                                        <SelectValue placeholder={t('admin.subscription_placeholder')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('subscription.all_plans')}</SelectItem>
                                        <SelectItem value="pro">{PLAN_LABELS.pro}</SelectItem>
                                        <SelectItem value="enterprise">{PLAN_LABELS.enterprise}</SelectItem>
                                    </SelectContent>
                                </Select>
                                {(invoiceFilterCount > 0 || invoiceSearch) && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => { setInvoiceFilters({ status: 'all', plan: 'all' }); setInvoiceSearch(''); }}
                                        data-testid="invoices-reset-filters"
                                    >
                                        <X className="w-4 h-4 mr-1" />{t('admin.reset_filters')}
                                    </Button>
                                )}
                                <span className="text-xs text-[#9CA3AF] ml-auto">{filteredInvoices.length} / {transactions.length}</span>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                                        <tr>
                                            {[
                                                { key: 'created_at', label: t('subscription.col_date'), testid: 'sort-invoice-date' },
                                                ...(user?.role === 'admin' ? [{ key: 'organization_name', label: t('subscription.col_company'), testid: 'sort-invoice-org' }] : []),
                                                { key: 'plan_id', label: t('subscription.col_plan'), testid: 'sort-invoice-plan' },
                                                { key: 'amount', label: t('subscription.col_amount'), testid: 'sort-invoice-amount' },
                                                { key: 'payment_status', label: t('subscription.col_status'), testid: 'sort-invoice-status' },
                                            ].map((col) => {
                                                const Icon = invoiceSortIcon(col.key);
                                                const active = invoiceSort.by === col.key;
                                                return (
                                                    <th key={col.key} className="px-4 py-2 text-left text-xs font-semibold text-[#4B5563] uppercase">
                                                        <button
                                                            onClick={() => setInvoiceSortFor(col.key)}
                                                            className={`inline-flex items-center gap-1 ${active ? 'text-[#002FA7]' : 'text-[#4B5563] hover:text-[#0A0A0A]'}`}
                                                            data-testid={col.testid}
                                                        >
                                                            {col.label}
                                                            <Icon className="w-3.5 h-3.5" />
                                                        </button>
                                                    </th>
                                                );
                                            })}
                                            <th className="px-4 py-2 text-left text-xs font-semibold text-[#4B5563] uppercase">{t('subscription.col_session')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#E5E7EB]">
                                        {filteredInvoices.length === 0 ? (
                                            <tr>
                                                <td colSpan={user?.role === 'admin' ? 6 : 5} className="px-4 py-8 text-center text-[#9CA3AF]">
                                                    {t('subscription.no_match')}
                                                </td>
                                            </tr>
                                        ) : filteredInvoices.map((tx) => (
                                            <tr key={tx.id || tx.session_id} className="hover:bg-[#F9FAFB]" data-testid={`invoice-row-${tx.session_id}`}>
                                                <td className="px-4 py-3 text-[#9CA3AF] text-xs">
                                                    {tx.created_at ? new Date(tx.created_at).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'fr-FR') : '-'}
                                                </td>
                                                {user?.role === 'admin' && (
                                                    <td className="px-4 py-3 text-[#0A0A0A]" data-testid={`invoice-org-${tx.session_id}`}>
                                                        {tx.organization_name || <span className="text-[#9CA3AF] italic">—</span>}
                                                    </td>
                                                )}
                                                <td className="px-4 py-3 text-[#0A0A0A]">{getPlanLabel(tx.plan_id)}</td>
                                                <td className="px-4 py-3 font-medium text-[#0A0A0A]">
                                                    {tx.amount} {tx.currency?.toUpperCase()}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                        ['paid', 'succeeded'].includes(tx.payment_status) ? 'bg-[#D1FAE5] text-[#059669]' :
                                                        tx.payment_status === 'pending' ? 'bg-[#FEF3C7] text-[#D97706]' :
                                                        'bg-[#FEE2E2] text-[#DC2626]'
                                                    }`}>
                                                        {tx.payment_status === 'paid' ? t('subscription.status_paid') : tx.payment_status === 'succeeded' ? t('subscription.status_succeeded') : tx.payment_status === 'pending' ? t('subscription.status_pending') : tx.payment_status === 'failed' ? t('subscription.status_failed') : tx.payment_status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 font-mono text-xs text-[#9CA3AF]">
                                                    {tx.session_id?.slice(0, 24)}...
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* FAQ */}
            <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">
                    {t('subscription.faq_title')}
                </h2>
                <div className="space-y-4">
                    <div>
                        <h3 className="font-medium text-[#0A0A0A]">{t('subscription.faq_q1')}</h3>
                        <p className="text-sm text-[#4B5563] mt-1">
                            <Trans i18nKey="subscription.faq_a1" components={{ strong: <strong /> }} />
                        </p>
                    </div>
                    <div>
                        <h3 className="font-medium text-[#0A0A0A]">{t('subscription.faq_q2')}</h3>
                        <p className="text-sm text-[#4B5563] mt-1">
                            {t('subscription.faq_a2')}
                        </p>
                    </div>
                    <div>
                        <h3 className="font-medium text-[#0A0A0A]">{t('subscription.faq_q3')}</h3>
                        <p className="text-sm text-[#4B5563] mt-1">
                            {t('subscription.faq_a3')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Multi-provider payment method dialog */}
            <Dialog open={!!paymentDialog} onOpenChange={(open) => !open && setPaymentDialog(null)}>
                <DialogContent className="max-w-lg" data-testid="payment-method-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('payment.dialog_title', { defaultValue: 'Choisir votre moyen de paiement' })}</DialogTitle>
                        <DialogDescription>
                            {t('payment.dialog_description', { defaultValue: 'Sélectionnez comment vous souhaitez régler. Vous serez redirigé vers le fournisseur choisi.' })}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <PaymentProviderSelector
                            providers={providers}
                            value={selectedProvider}
                            onChange={setSelectedProvider}
                            mode={paymentDialog?.mode || 'subscription'}
                            currency={paymentDialog?.currency || 'eur'}
                        />
                    </div>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setPaymentDialog(null)}
                            data-testid="payment-dialog-cancel"
                        >
                            {t('common.cancel', { defaultValue: 'Annuler' })}
                        </Button>
                        <Button
                            onClick={confirmPaymentDialog}
                            data-testid="payment-dialog-continue"
                            disabled={
                                !selectedProvider ||
                                !providers.some((p) => p.id === selectedProvider && p.configured
                                    && (paymentDialog?.mode === 'subscription' ? p.supports_subscriptions : p.supports_one_time)
                                )
                            }
                        >
                            {t('payment.continue', { defaultValue: 'Continuer' })}
                            <ArrowUpRight className="w-4 h-4 ml-2" />
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
